<template>
  <div class="aggregate-dashboard" :style="themeStyle">
    <section class="header-card">
      <div class="header-main">
        <p class="header-eyebrow">{{ eyebrow }}</p>
        <div class="header-title-row">
          <div>
            <h1>{{ title }}</h1>
            <p class="header-description">{{ description }}</p>
          </div>

          <router-link :to="{ name: homeRouteName }" class="btn btn-outline-primary header-home-link">
            ホーム
          </router-link>
        </div>
      </div>

      <div class="header-meta">
        <div class="meta-chip">
          <span class="meta-chip-label">対象期間</span>
          <strong>{{ selectedPeriodLabel }}</strong>
        </div>
        <div class="meta-chip">
          <span class="meta-chip-label">期間長</span>
          <strong>{{ rangeMonthCount }}か月</strong>
        </div>
        <div class="meta-chip">
          <span class="meta-chip-label">集計基準</span>
          <strong>{{ periodBasisLabel }}</strong>
        </div>
        <div v-if="lastLoadedAt" class="meta-chip meta-chip-muted">
          <span class="meta-chip-label">更新時刻</span>
          <strong>{{ formatDateTime(lastLoadedAt) }}</strong>
        </div>
      </div>
    </section>

    <section class="panel-card filter-card">
      <div class="panel-heading panel-heading-tight">
        <div>
          <h2>表示条件</h2>
          <p>開始月から終了月までの範囲で集計します。期間の逆転は自動で補正します。</p>
        </div>
      </div>

      <div class="filter-grid">
        <label class="filter-field">
          <span>開始月</span>
          <input v-model="searchStartYm" type="month" class="form-control" :disabled="loading" />
        </label>

        <div class="range-separator" aria-hidden="true">〜</div>

        <label class="filter-field">
          <span>終了月</span>
          <input v-model="searchEndYm" type="month" class="form-control" :disabled="loading" />
        </label>

        <div class="filter-actions">
          <button type="button" class="btn btn-primary" :disabled="loading" @click="applyFilters">
            集計を更新
          </button>
          <button type="button" class="btn btn-light" :disabled="loading" @click="resetFilters">
            今期に戻す
          </button>
        </div>
      </div>

      <div class="preset-row">
        <button
          v-for="preset in presetRanges"
          :key="preset.key"
          type="button"
          :class="['preset-button', { active: isPresetActive(preset.range) }]"
          :disabled="loading"
          @click="applyPreset(preset.range)"
        >
          {{ preset.label }}
        </button>
      </div>
    </section>

    <section class="tab-strip" aria-label="集計軸">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        type="button"
        :class="['tab-button', { active: activeTabKey === tab.key }]"
        :aria-pressed="activeTabKey === tab.key"
        :disabled="loading && activeTabKey !== tab.key"
        @click="selectTab(tab.key)"
      >
        <span class="tab-label">{{ tab.label }}</span>
        <small class="tab-help">{{ tab.shortDescription }}</small>
      </button>
    </section>

    <section v-if="currentRows.length" class="summary-grid">
      <article v-for="card in summaryCards" :key="card.label" class="summary-card">
        <span class="summary-label">{{ card.label }}</span>
        <strong class="summary-value">{{ card.value }}</strong>
        <p class="summary-note">{{ card.note }}</p>
      </article>
    </section>

    <section class="panel-card visual-card">
      <div class="panel-heading panel-heading-tight">
        <div>
          <h2>{{ activeTab.chartTitle }}</h2>
        </div>

        <div v-if="showChartScopeToggle" class="scope-switch">
          <span class="scope-copy">表示件数</span>
          <div class="scope-buttons">
            <button
              type="button"
              :class="['scope-button', { active: chartScope === 'top' }]"
              @click="chartScope = 'top'"
            >
              上位{{ categoryChartLimit }}件
            </button>
            <button
              type="button"
              :class="['scope-button', { active: chartScope === 'all' }]"
              @click="chartScope = 'all'"
            >
              全件
            </button>
          </div>
        </div>
      </div>

      <div v-if="errorMessage" class="alert alert-danger mb-0">
        {{ errorMessage }}
      </div>

      <div v-else-if="loading" class="empty-state">
        <div class="spinner-border text-primary" role="status" aria-hidden="true"></div>
        <p>集計データを読み込んでいます。</p>
      </div>

      <div v-else-if="!currentRows.length" class="empty-state">
        <h3>対象データがありません</h3>
        <p>条件を変更して再度集計してください。</p>
      </div>

      <div v-else class="visual-grid">
        <div :class="['chart-panel', { 'chart-panel-scrollable': shouldScrollChart }]">
          <div class="chart-scroll-area">
            <div class="chart-canvas-shell" :style="chartCanvasShellStyle">
              <canvas ref="chartCanvas" />
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="panel-card table-card">
      <div class="panel-heading panel-heading-tight">
        <div>
          <h2>集計明細</h2>
          <p>グラフと同じ条件の全件を表形式で確認できます。</p>
        </div>

        <div v-if="currentRows.length" class="table-heading-actions">
          <div class="table-overview">
            <span>{{ currentRows.length }}件</span>
            <strong>{{ formatCurrency(totalValue) }}</strong>
          </div>

          <button
            type="button"
            class="btn btn-success btn-sm export-button"
            :aria-label="`${title} ${activeTab.label} の集計明細をExcel出力`"
            :disabled="loading || exporting || !currentRows.length"
            @click="exportDetailToExcel"
          >
            {{ exporting ? '出力中...' : 'Excel出力' }}
          </button>
        </div>
      </div>

      <div v-if="exportErrorMessage" class="alert alert-danger table-export-alert">
        {{ exportErrorMessage }}
      </div>

      <div v-if="currentRows.length" class="table-wrapper">
        <table class="aggregate-table">
          <caption class="visually-hidden">
            {{ title }} {{ activeTab.label }} の集計表
          </caption>

          <thead v-if="activeTab.isMonthly">
            <tr>
              <th>年月</th>
              <th class="text-end">{{ amountLabel }}</th>
              <th class="text-end">構成比</th>
              <th class="text-end">平均との差</th>
            </tr>
          </thead>

          <thead v-else>
            <tr>
              <th>順位</th>
              <th>{{ activeTab.tableLabel }}</th>
              <th class="text-end">{{ amountLabel }}</th>
              <th class="text-end">構成比</th>
              <th>シェア</th>
              <th class="text-end">累積構成比</th>
            </tr>
          </thead>

          <tbody v-if="activeTab.isMonthly">
            <tr
              v-for="row in currentRows"
              :key="row.key"
              :class="{ highlight: row.rank === 1 }"
            >
              <td>{{ row.label }}</td>
              <td class="text-end amount-cell">{{ formatCurrency(row.value) }}</td>
              <td class="text-end">{{ formatPercent(row.share) }}</td>
              <td class="text-end" :class="differenceClass(row.deltaFromAverage)">
                {{ formatSignedCurrency(row.deltaFromAverage) }}
              </td>
            </tr>
          </tbody>

          <tbody v-else>
            <tr
              v-for="row in currentRows"
              :key="row.key"
              :class="{ highlight: row.rank === 1 }"
            >
              <td>{{ row.rank }}</td>
              <td class="item-label" :title="row.label">{{ row.label }}</td>
              <td class="text-end amount-cell">{{ formatCurrency(row.value) }}</td>
              <td class="text-end">{{ formatPercent(row.share) }}</td>
              <td>
                <div
                  class="share-meter"
                  role="img"
                  :aria-label="`${row.label} の構成比 ${formatPercent(row.share)}`"
                >
                  <span :style="{ width: `${Math.max(row.share, row.value > 0 ? 2 : 0)}%` }"></span>
                </div>
              </td>
              <td class="text-end">{{ formatPercent(row.cumulativeShare) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  </div>
</template>

<script>
import { Chart, registerables } from 'chart.js';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch, watchEffect } from 'vue';
import * as XLSX from 'xlsx';
import {
  CATEGORY_CHART_LIMIT,
  buildPresetRanges,
  countMonthsInRange,
  formatCompactCurrency,
  formatCurrency,
  formatDateTime,
  formatPeriodLabel,
  formatPercent,
  getDefaultYmRange,
  normalizeAggregateItems,
  normalizeYmRange,
} from '@/utils/aggregateDashboard';

Chart.register(...registerables);

const amountNumberFormat = '#,##0;[Red]-#,##0;0';
const percentNumberFormat = '0.0%';

const defaultTheme = {
  accent: '#1f6fb2',
  accentSoft: '#dceeff',
  accentStrong: '#164f7d',
  accentRgb: '31, 111, 178',
  surface: '#ffffff',
  surfaceAlt: '#f4f8fc',
  border: '#d3deea',
  textMuted: '#61758a',
};

const toRgba = (rgbValue, opacity) => `rgba(${rgbValue}, ${opacity})`;
const waitForFrame = () => new Promise((resolve) => window.requestAnimationFrame(resolve));
const toExcelPercent = (value) => Number(value || 0) / 100;
const toExcelAmount = (value) => Number(value || 0);

const sanitizeFilePart = (value) => {
  return String(value || '')
    .replace(/[\\/:*?"<>|]/g, '_')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 80);
};

const formatTimestampForFilename = (dateValue) => {
  const date = dateValue || new Date();
  const parts = [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
    String(date.getHours()).padStart(2, '0'),
    String(date.getMinutes()).padStart(2, '0'),
  ];

  return parts.join('');
};

const setCellNumberFormat = (worksheet, rowIndex, columnIndex, numberFormat) => {
  const cellAddress = XLSX.utils.encode_cell({ r: rowIndex, c: columnIndex });
  if (!worksheet[cellAddress]) return;

  worksheet[cellAddress].z = numberFormat;
};

const buildSumCell = (columnLetter, dataRowCount, fallbackValue) => {
  const lastDataRowNumber = dataRowCount + 1;

  return {
    t: 'n',
    f: `SUM(${columnLetter}2:${columnLetter}${lastDataRowNumber})`,
    v: Number(fallbackValue || 0),
  };
};

export default {
  name: 'AggregateSalesDashboard',
  props: {
    title: {
      type: String,
      required: true,
    },
    eyebrow: {
      type: String,
      default: 'Aggregate Dashboard',
    },
    description: {
      type: String,
      default: '',
    },
    tabs: {
      type: Array,
      required: true,
    },
    theme: {
      type: Object,
      default: () => ({}),
    },
    homeRouteName: {
      type: String,
      default: 'Home',
    },
    periodBasisLabel: {
      type: String,
      default: '作業年月',
    },
  },
  setup(props) {
    const mergedTheme = computed(() => ({ ...defaultTheme, ...props.theme }));
    const defaultRange = getDefaultYmRange();
    const presetRanges = buildPresetRanges();
    const searchStartYm = ref(defaultRange.startYm);
    const searchEndYm = ref(defaultRange.endYm);
    const appliedFilters = ref({ ...defaultRange });
    const activeTabKey = ref(props.tabs[0]?.key || '');
    const loading = ref(false);
    const errorMessage = ref('');
    const chartCanvas = ref(null);
    const chartInstance = ref(null);
    const chartScope = ref('top');
    const datasets = ref({});
    const lastLoadedAt = ref(null);
    const loadVersion = ref(0);
    const chartRenderVersion = ref(0);
    const exporting = ref(false);
    const exportErrorMessage = ref('');

    const themeStyle = computed(() => ({
      '--dashboard-accent': mergedTheme.value.accent,
      '--dashboard-accent-soft': mergedTheme.value.accentSoft,
      '--dashboard-accent-strong': mergedTheme.value.accentStrong,
      '--dashboard-accent-rgb': mergedTheme.value.accentRgb,
      '--dashboard-surface': mergedTheme.value.surface,
      '--dashboard-surface-alt': mergedTheme.value.surfaceAlt,
      '--dashboard-border': mergedTheme.value.border,
      '--dashboard-muted': mergedTheme.value.textMuted,
    }));

    const activeTab = computed(() => {
      return props.tabs.find((tab) => tab.key === activeTabKey.value) || props.tabs[0];
    });

    const currentDataset = computed(() => datasets.value[activeTabKey.value] || { items: [], total: 0 });
    const totalValue = computed(() => currentDataset.value.total || 0);
    const amountLabel = computed(() => activeTab.value?.amountLabel || '売上');
    const totalAmountLabel = computed(() => activeTab.value?.totalAmountLabel || '総売上');
    const averageValue = computed(() => {
      if (!currentDataset.value.items?.length) return 0;
      return totalValue.value / currentDataset.value.items.length;
    });
    const rangeMonthCount = computed(() => countMonthsInRange(appliedFilters.value.startYm, appliedFilters.value.endYm));
    const selectedPeriodLabel = computed(() => formatPeriodLabel(appliedFilters.value.startYm, appliedFilters.value.endYm));

    const currentRows = computed(() => {
      const items = currentDataset.value.items || [];

      if (!activeTab.value?.isMonthly) {
        return items;
      }

      return items.map((item) => ({
        ...item,
        deltaFromAverage: item.value - averageValue.value,
      }));
    });

    const chartRows = computed(() => {
      if (activeTab.value?.isMonthly || chartScope.value === 'all') {
        return currentRows.value;
      }

      return currentRows.value.slice(0, CATEGORY_CHART_LIMIT);
    });

    const showChartScopeToggle = computed(() => {
      return !activeTab.value?.isMonthly && currentRows.value.length > CATEGORY_CHART_LIMIT;
    });

    const shouldScrollChart = computed(() => {
      return !activeTab.value?.isMonthly
        && chartScope.value === 'all'
        && chartRows.value.length > CATEGORY_CHART_LIMIT;
    });

    const chartCanvasShellStyle = computed(() => {
      if (!shouldScrollChart.value) {
        return { height: '100%' };
      }

      const rowHeight = 30;
      const chartPadding = 28;
      const minimumHeight = 264;

      return {
        height: `${Math.max(minimumHeight, (chartRows.value.length * rowHeight) + chartPadding)}px`,
      };
    });

    const summaryCards = computed(() => {
      if (!currentRows.value.length) return [];

      const topItem = [...currentRows.value].sort((left, right) => right.value - left.value)[0];
      const topThree = [...currentRows.value]
        .sort((left, right) => right.value - left.value)
        .slice(0, 3)
        .reduce((sum, item) => sum + item.value, 0);
      const topThreeShare = totalValue.value > 0 ? (topThree / totalValue.value) * 100 : 0;

      return [
        {
          label: totalAmountLabel.value,
          value: formatCompactCurrency(totalValue.value),
          note: selectedPeriodLabel.value,
        },
        {
          label: activeTab.value.isMonthly ? '月平均' : `平均${amountLabel.value}`,
          value: formatCompactCurrency(averageValue.value),
          note: `${currentRows.value.length}${activeTab.value.isMonthly ? 'か月' : '項目'}を平均化`,
        },
        {
          label: activeTab.value.isMonthly ? 'ピーク月' : '最大項目',
          value: topItem?.label || '対象なし',
          note: topItem ? `${formatCompactCurrency(topItem.value)} / ${formatPercent(topItem.share)}` : '対象なし',
        },
        {
          label: '上位3構成比',
          value: formatPercent(topThreeShare),
          note: activeTab.value.isMonthly ? `${amountLabel.value}が集中した月を把握` : `${amountLabel.value}の偏りを把握`,
        },
      ];
    });

    const formatSignedCurrency = (value) => {
      const prefix = Number(value || 0) >= 0 ? '+' : '-';
      return `${prefix}${formatCurrency(Math.abs(Number(value || 0)))}`;
    };

    const differenceClass = (value) => {
      if (value > 0) return 'difference-positive';
      if (value < 0) return 'difference-negative';
      return '';
    };

    const resolveSearchRange = () => {
      const fallbackStart = searchStartYm.value || appliedFilters.value.startYm || defaultRange.startYm;
      const fallbackEnd = searchEndYm.value || appliedFilters.value.endYm || defaultRange.endYm;
      return normalizeYmRange(fallbackStart, fallbackEnd);
    };

    const isPresetActive = (range) => {
      const normalizedPreset = normalizeYmRange(range.startYm, range.endYm);
      const normalizedSearch = normalizeYmRange(searchStartYm.value, searchEndYm.value);

      return normalizedPreset.startYm === normalizedSearch.startYm
        && normalizedPreset.endYm === normalizedSearch.endYm;
    };

    const destroyChart = () => {
      chartRenderVersion.value += 1;

      if (chartInstance.value) {
        chartInstance.value.stop();
        chartInstance.value.destroy();
        chartInstance.value = null;
      }
    };

    const buildCategoryChartConfig = () => {
      const labels = chartRows.value.map((row) => row.label);
      const values = chartRows.value.map((row) => row.value);

      return {
        type: 'bar',
        data: {
          labels,
          datasets: [
            {
              label: amountLabel.value,
              data: values,
              backgroundColor: values.map((_, index) => {
                return index === 0
                  ? toRgba(mergedTheme.value.accentRgb, 0.9)
                  : toRgba(mergedTheme.value.accentRgb, Math.max(0.28, 0.68 - (index * 0.045)));
              }),
              borderRadius: 8,
              borderSkipped: false,
              barThickness: 14,
              maxBarThickness: 18,
            },
          ],
        },
        options: {
          indexAxis: 'y',
          responsive: true,
          maintainAspectRatio: false,
          animation: false,
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              callbacks: {
                label: (context) => `${amountLabel.value} ${formatCurrency(context.raw)}`,
              },
            },
          },
          scales: {
            x: {
              beginAtZero: true,
              ticks: {
                callback: (value) => formatCompactCurrency(value),
              },
              grid: {
                color: toRgba(mergedTheme.value.accentRgb, 0.1),
              },
            },
            y: {
              ticks: {
                autoSkip: false,
                callback: (_, index) => {
                  const label = labels[index] || '';
                  return label.length > 18 ? `${label.slice(0, 18)}...` : label;
                },
              },
              grid: {
                display: false,
              },
            },
          },
        },
      };
    };

    const buildMonthlyChartConfig = () => {
      const labels = chartRows.value.map((row) => row.label);
      const values = chartRows.value.map((row) => row.value);
      const averageLine = values.map(() => averageValue.value);
      const minimumValue = Math.min(...values, 0);

      return {
        type: 'line',
        data: {
          labels,
          datasets: [
            {
              label: amountLabel.value,
              data: values,
              tension: 0.25,
              borderWidth: 3,
              borderColor: mergedTheme.value.accent,
              backgroundColor: toRgba(mergedTheme.value.accentRgb, 0.16),
              fill: true,
              pointRadius: 3,
              pointHoverRadius: 4,
              pointBackgroundColor: mergedTheme.value.accentStrong,
            },
            {
              label: '平均',
              data: averageLine,
              borderColor: toRgba(mergedTheme.value.accentRgb, 0.45),
              borderWidth: 2,
              borderDash: [6, 6],
              pointRadius: 0,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: false,
          plugins: {
            legend: {
              position: 'top',
              labels: {
                usePointStyle: true,
                boxWidth: 8,
              },
            },
            tooltip: {
              callbacks: {
                label: (context) => `${context.dataset.label} ${formatCurrency(context.raw)}`,
              },
            },
          },
          scales: {
            x: {
              grid: {
                display: false,
              },
            },
            y: {
              beginAtZero: minimumValue <= 0,
              grace: '10%',
              ticks: {
                callback: (value) => formatCompactCurrency(value),
              },
              grid: {
                color: toRgba(mergedTheme.value.accentRgb, 0.1),
              },
            },
          },
        },
      };
    };

    const renderChart = async () => {
      if (loading.value || !chartRows.value.length) {
        destroyChart();
        return;
      }

      const renderVersion = chartRenderVersion.value + 1;
      chartRenderVersion.value = renderVersion;

      await nextTick();
      await waitForFrame();

      if (renderVersion !== chartRenderVersion.value) return;

      const canvas = chartCanvas.value;
      if (!canvas || !canvas.isConnected) return;

      const container = canvas.parentElement || canvas;
      let { width, height } = container.getBoundingClientRect();

      if (!width || !height) {
        await waitForFrame();
        if (renderVersion !== chartRenderVersion.value) return;
        ({ width, height } = container.getBoundingClientRect());
      }

      if (!width || !height) return;

      const context = canvas.getContext('2d');
      if (!context) return;

      if (chartInstance.value) {
        chartInstance.value.stop();
        chartInstance.value.destroy();
        chartInstance.value = null;
      }

      if (renderVersion !== chartRenderVersion.value) return;

      const chartConfig = activeTab.value.isMonthly
        ? buildMonthlyChartConfig()
        : buildCategoryChartConfig();

      chartInstance.value = new Chart(context, chartConfig);
    };

    const fetchTabData = async (tab) => {
      const response = await tab.fetcher({
        start_ym: appliedFilters.value.startYm,
        end_ym: appliedFilters.value.endYm,
      });
      return normalizeAggregateItems({
        items: response?.data?.items || [],
        labels: response?.data?.x_axis || [],
        values: response?.data?.y_axis || [],
        tab,
      });
    };

    const buildExportDetailRows = () => {
      if (activeTab.value?.isMonthly) {
        return [
          ['年月', amountLabel.value, '構成比', '平均との差'],
          ...currentRows.value.map((row) => [
            row.label,
            toExcelAmount(row.value),
            toExcelPercent(row.share),
            toExcelAmount(row.deltaFromAverage),
          ]),
          [
            '合計',
            buildSumCell('B', currentRows.value.length, totalValue.value),
            buildSumCell('C', currentRows.value.length, 1),
            '',
          ],
        ];
      }

      return [
        ['順位', activeTab.value.tableLabel, amountLabel.value, '構成比', '累積構成比'],
        ...currentRows.value.map((row) => [
          row.rank,
          row.label,
          toExcelAmount(row.value),
          toExcelPercent(row.share),
          toExcelPercent(row.cumulativeShare),
        ]),
        [
          '',
          '合計',
          buildSumCell('C', currentRows.value.length, totalValue.value),
          buildSumCell('D', currentRows.value.length, 1),
          '',
        ],
      ];
    };

    const applyDetailWorksheetSettings = (worksheet) => {
      const range = worksheet['!ref'];
      if (!range) return;

      const columnCount = activeTab.value?.isMonthly ? 4 : 5;
      worksheet['!autofilter'] = {
        ref: XLSX.utils.encode_range({
          s: { r: 0, c: 0 },
          e: { r: currentRows.value.length, c: columnCount - 1 },
        }),
      };
      worksheet['!cols'] = activeTab.value?.isMonthly
        ? [
            { wch: 16 },
            { wch: 16 },
            { wch: 12 },
            { wch: 16 },
          ]
        : [
            { wch: 8 },
            { wch: 34 },
            { wch: 16 },
            { wch: 12 },
            { wch: 14 },
          ];

      currentRows.value.forEach((_, index) => {
        const rowIndex = index + 1;

        if (activeTab.value?.isMonthly) {
          setCellNumberFormat(worksheet, rowIndex, 1, amountNumberFormat);
          setCellNumberFormat(worksheet, rowIndex, 2, percentNumberFormat);
          setCellNumberFormat(worksheet, rowIndex, 3, amountNumberFormat);
          return;
        }

        setCellNumberFormat(worksheet, rowIndex, 2, amountNumberFormat);
        setCellNumberFormat(worksheet, rowIndex, 3, percentNumberFormat);
        setCellNumberFormat(worksheet, rowIndex, 4, percentNumberFormat);
      });

      const totalRowIndex = currentRows.value.length + 1;

      if (activeTab.value?.isMonthly) {
        setCellNumberFormat(worksheet, totalRowIndex, 1, amountNumberFormat);
        setCellNumberFormat(worksheet, totalRowIndex, 2, percentNumberFormat);
        return;
      }

      setCellNumberFormat(worksheet, totalRowIndex, 2, amountNumberFormat);
      setCellNumberFormat(worksheet, totalRowIndex, 3, percentNumberFormat);
    };

    const buildExportMetadataRows = (exportedAt) => {
      return [
        ['項目', '内容'],
        ['画面', props.title],
        ['集計軸', activeTab.value?.label || ''],
        ['集計基準', props.periodBasisLabel],
        ['対象期間', selectedPeriodLabel.value],
        ['開始月', appliedFilters.value.startYm],
        ['終了月', appliedFilters.value.endYm],
        ['出力件数', currentRows.value.length],
        [totalAmountLabel.value, toExcelAmount(totalValue.value)],
        ['データ取得時刻', lastLoadedAt.value ? formatDateTime(lastLoadedAt.value) : ''],
        ['出力日時', formatDateTime(exportedAt)],
        ['出力者', localStorage.getItem('userName') || ''],
      ];
    };

    const applyMetadataWorksheetSettings = (worksheet) => {
      worksheet['!cols'] = [
        { wch: 18 },
        { wch: 42 },
      ];
      setCellNumberFormat(worksheet, 6, 1, '#,##0');
      setCellNumberFormat(worksheet, 7, 1, amountNumberFormat);
    };

    const buildExportFilename = (exportedAt) => {
      const fileParts = [
        props.title,
        activeTab.value?.label || '',
        selectedPeriodLabel.value,
        formatTimestampForFilename(exportedAt),
      ].map(sanitizeFilePart).filter(Boolean);

      return `${fileParts.join('_')}.xlsx`;
    };

    const exportDetailToExcel = async () => {
      if (loading.value || exporting.value) return;

      if (!currentRows.value.length) {
        exportErrorMessage.value = '出力できる集計明細がありません。条件を変更して再度集計してください。';
        return;
      }

      exporting.value = true;
      exportErrorMessage.value = '';
      await nextTick();

      try {
        const exportedAt = new Date();
        const detailWorksheet = XLSX.utils.aoa_to_sheet(buildExportDetailRows());
        const metadataWorksheet = XLSX.utils.aoa_to_sheet(buildExportMetadataRows(exportedAt));
        const workbook = XLSX.utils.book_new();

        applyDetailWorksheetSettings(detailWorksheet);
        applyMetadataWorksheetSettings(metadataWorksheet);

        workbook.Props = {
          Title: `${props.title} ${activeTab.value?.label || ''} 集計明細`,
          Subject: selectedPeriodLabel.value,
          Author: localStorage.getItem('userName') || 'measure_rep_mng',
          CreatedDate: exportedAt,
        };

        XLSX.utils.book_append_sheet(workbook, detailWorksheet, '集計明細');
        XLSX.utils.book_append_sheet(workbook, metadataWorksheet, '出力条件');
        XLSX.writeFile(workbook, buildExportFilename(exportedAt), { compression: true });
      } catch (error) {
        console.error('集計明細のExcel出力に失敗しました:', error);
        exportErrorMessage.value = 'Excel出力に失敗しました。時間をおいて再度お試しください。';
      } finally {
        exporting.value = false;
      }
    };

    const loadTabData = async (tabKey, force = false) => {
      const tab = props.tabs.find((item) => item.key === tabKey);
      if (!tab) return;

      if (!force && datasets.value[tabKey]) {
        return;
      }

      const versionAtRequestStart = loadVersion.value;
      loading.value = true;
      errorMessage.value = '';

      try {
        const data = await fetchTabData(tab);
        if (versionAtRequestStart !== loadVersion.value) return;

        datasets.value = {
          ...datasets.value,
          [tabKey]: data,
        };
        lastLoadedAt.value = new Date();
      } catch (error) {
        if (versionAtRequestStart !== loadVersion.value) return;

        console.error('集計情報の取得に失敗しました:', error);
        errorMessage.value = '集計データの取得に失敗しました。時間をおいて再度お試しください。';
      } finally {
        if (versionAtRequestStart === loadVersion.value) {
          loading.value = false;
        }
      }
    };

    const applyFilters = async () => {
      const normalizedRange = resolveSearchRange();
      searchStartYm.value = normalizedRange.startYm;
      searchEndYm.value = normalizedRange.endYm;
      datasets.value = {};
      chartScope.value = 'top';
      exportErrorMessage.value = '';
      appliedFilters.value = normalizedRange;
      loadVersion.value += 1;
      await loadTabData(activeTabKey.value, true);
    };

    const applyPreset = async (range) => {
      const normalizedRange = normalizeYmRange(range.startYm, range.endYm);
      searchStartYm.value = normalizedRange.startYm;
      searchEndYm.value = normalizedRange.endYm;
      await applyFilters();
    };

    const resetFilters = async () => {
      searchStartYm.value = defaultRange.startYm;
      searchEndYm.value = defaultRange.endYm;
      await applyFilters();
    };

    const selectTab = async (tabKey) => {
      activeTabKey.value = tabKey;
      exportErrorMessage.value = '';
      await loadTabData(tabKey);
    };

    watch(chartRows, async () => {
      if (!loading.value) {
        await renderChart();
      }
    }, { deep: true, flush: 'post' });

    watch(activeTabKey, async () => {
      if (!loading.value) {
        await renderChart();
      }
    }, { flush: 'post' });

    watch(loading, async (isLoading) => {
      if (isLoading) {
        destroyChart();
        return;
      }

      await renderChart();
    }, { flush: 'post' });

    watchEffect(() => {
      document.title = props.title;
    });

    onMounted(async () => {
      await loadTabData(activeTabKey.value, true);
    });

    onBeforeUnmount(() => {
      destroyChart();
    });

    return {
      activeTab,
      activeTabKey,
      amountLabel,
      applyFilters,
      applyPreset,
      categoryChartLimit: CATEGORY_CHART_LIMIT,
      chartCanvas,
      chartCanvasShellStyle,
      chartScope,
      currentRows,
      differenceClass,
      errorMessage,
      exportDetailToExcel,
      exportErrorMessage,
      exporting,
      formatCurrency,
      formatDateTime,
      formatPercent,
      formatSignedCurrency,
      isPresetActive,
      lastLoadedAt,
      loading,
      presetRanges,
      rangeMonthCount,
      resetFilters,
      searchEndYm,
      searchStartYm,
      selectedPeriodLabel,
      selectTab,
      showChartScopeToggle,
      shouldScrollChart,
      summaryCards,
      themeStyle,
      totalValue,
    };
  },
};
</script>

<style scoped>
.aggregate-dashboard {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  color: #17324d;
}

.header-card,
.panel-card {
  border: 1px solid var(--dashboard-border);
  border-radius: 18px;
  background: var(--dashboard-surface);
  box-shadow: 0 12px 32px rgba(16, 44, 70, 0.07);
}

.header-card {
  display: grid;
  grid-template-columns: minmax(0, 1.7fr) minmax(300px, 0.9fr);
  gap: 16px;
  padding: 18px 20px;
  background:
    radial-gradient(circle at top right, rgba(var(--dashboard-accent-rgb), 0.14), transparent 26%),
    linear-gradient(135deg, rgba(var(--dashboard-accent-rgb), 0.1), transparent 48%),
    var(--dashboard-surface);
}

.header-eyebrow {
  margin: 0 0 6px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--dashboard-accent-strong);
}

.header-title-row {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
}

.header-main h1 {
  margin: 0 0 6px;
  font-size: 28px;
  font-weight: 700;
  line-height: 1.2;
}

.header-description {
  margin: 0;
  max-width: 780px;
  font-size: 13px;
  line-height: 1.7;
  color: var(--dashboard-muted);
}

.header-home-link {
  white-space: nowrap;
  min-width: 120px;
  border-color: rgba(var(--dashboard-accent-rgb), 0.28);
  color: var(--dashboard-accent-strong);
}

.header-meta {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  align-content: start;
}

.meta-chip {
  padding: 10px 12px;
  border-radius: 14px;
  background: rgba(var(--dashboard-accent-rgb), 0.08);
  border: 1px solid rgba(var(--dashboard-accent-rgb), 0.12);
}

.meta-chip-muted {
  background: var(--dashboard-surface-alt);
}

.meta-chip-label {
  display: block;
  margin-bottom: 2px;
  font-size: 11px;
  color: var(--dashboard-muted);
}

.panel-card {
  padding: 16px;
}

.table-card {
  background: linear-gradient(180deg, #ffffff, rgba(var(--dashboard-accent-rgb), 0.015));
}

.panel-heading {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 14px;
  margin-bottom: 14px;
}

.panel-heading-tight {
  margin-bottom: 12px;
}

.panel-heading h2 {
  margin: 0 0 4px;
  font-size: 18px;
  font-weight: 700;
}

.panel-heading p {
  margin: 0;
  font-size: 13px;
  color: var(--dashboard-muted);
  line-height: 1.6;
}

.filter-grid {
  display: grid;
  grid-template-columns: minmax(170px, 190px) 24px minmax(170px, 190px) minmax(240px, auto);
  gap: 10px;
  align-items: end;
}

.filter-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-weight: 600;
}

.filter-field span {
  font-size: 13px;
}

.range-separator {
  padding-bottom: 10px;
  text-align: center;
  color: var(--dashboard-muted);
}

.filter-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.preset-row {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 10px;
}

.preset-button {
  border: 1px solid var(--dashboard-border);
  border-radius: 999px;
  background: var(--dashboard-surface);
  padding: 6px 12px;
  font-size: 12px;
  color: var(--dashboard-muted);
  transition: border-color 0.2s ease, background-color 0.2s ease, color 0.2s ease;
}

.preset-button.active {
  border-color: rgba(var(--dashboard-accent-rgb), 0.32);
  background: rgba(var(--dashboard-accent-rgb), 0.08);
  color: var(--dashboard-accent-strong);
  font-weight: 700;
}

.tab-strip {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 2px;
}

.tab-button {
  min-width: 134px;
  padding: 10px 12px;
  border: 1px solid var(--dashboard-border);
  border-radius: 14px;
  background: var(--dashboard-surface);
  text-align: left;
  transition: transform 0.2s ease, border-color 0.2s ease, background-color 0.2s ease;
}

.tab-button:hover:not(:disabled) {
  transform: translateY(-1px);
  border-color: rgba(var(--dashboard-accent-rgb), 0.28);
}

.tab-button.active {
  border-color: rgba(var(--dashboard-accent-rgb), 0.38);
  background: linear-gradient(180deg, rgba(var(--dashboard-accent-rgb), 0.12), rgba(var(--dashboard-accent-rgb), 0.04));
}

.tab-label {
  display: block;
  margin-bottom: 4px;
  font-size: 13px;
  font-weight: 700;
}

.tab-help {
  display: block;
  font-size: 11px;
  color: var(--dashboard-muted);
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
}

.summary-card {
  padding: 12px 14px;
  border-radius: 16px;
  border: 1px solid rgba(var(--dashboard-accent-rgb), 0.12);
  background: linear-gradient(180deg, rgba(var(--dashboard-accent-rgb), 0.07), rgba(var(--dashboard-accent-rgb), 0.02));
}

.summary-label {
  display: block;
  margin-bottom: 6px;
  font-size: 11px;
  font-weight: 700;
  color: var(--dashboard-muted);
}

.summary-value {
  display: block;
  margin-bottom: 4px;
  font-size: 22px;
  line-height: 1.2;
}

.summary-note {
  margin: 0;
  font-size: 12px;
  color: var(--dashboard-muted);
  line-height: 1.5;
}

.scope-switch {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: flex-end;
}

.scope-copy {
  color: var(--dashboard-muted);
  font-size: 12px;
}

.scope-buttons {
  display: inline-flex;
  padding: 3px;
  border-radius: 999px;
  background: var(--dashboard-surface-alt);
}

.scope-button {
  border: none;
  background: transparent;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  color: var(--dashboard-muted);
}

.scope-button.active {
  background: var(--dashboard-surface);
  box-shadow: 0 4px 12px rgba(16, 44, 70, 0.08);
  color: var(--dashboard-accent-strong);
  font-weight: 700;
}

.visual-grid {
  display: block;
}

.chart-panel {
  height: 264px;
  padding: 8px 10px;
  border-radius: 16px;
  background: linear-gradient(180deg, rgba(var(--dashboard-accent-rgb), 0.03), #ffffff);
  border: 1px solid rgba(var(--dashboard-accent-rgb), 0.08);
  overflow: hidden;
}

.chart-scroll-area {
  height: 100%;
}

.chart-panel-scrollable .chart-scroll-area {
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  scrollbar-gutter: stable;
  padding-right: 4px;
}

.chart-canvas-shell {
  min-height: 100%;
}

.chart-panel canvas {
  width: 100% !important;
  height: 100% !important;
}

.empty-state {
  min-height: 220px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  text-align: center;
  color: var(--dashboard-muted);
}

.table-overview {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--dashboard-muted);
  font-size: 12px;
}

.table-overview strong {
  color: var(--dashboard-accent-strong);
  font-size: 16px;
}

.table-heading-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
}

.export-button {
  min-width: 96px;
}

.table-export-alert {
  margin: 0 0 12px;
}

.table-wrapper {
  max-height: min(56vh, 560px);
  overflow: auto;
  position: relative;
  overscroll-behavior: contain;
  scrollbar-gutter: stable both-edges;
  -webkit-overflow-scrolling: touch;
  border-radius: 14px;
  border: 1px solid var(--dashboard-border);
  background: #ffffff;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.8);
}

.aggregate-table {
  width: 100%;
  min-width: 760px;
  border-collapse: separate;
  border-spacing: 0;
  background: #ffffff;
}

.aggregate-table thead th {
  position: sticky;
  top: 0;
  z-index: 4;
  background: linear-gradient(180deg, #eef5fb 0%, #e8f1f9 100%);
  background-clip: padding-box;
  box-shadow: inset 0 -1px 0 #d8e4ef, 0 1px 0 rgba(255, 255, 255, 0.72);
  color: #1d3d5b;
  font-size: 12px;
  font-weight: 700;
  isolation: isolate;
}

.aggregate-table th,
.aggregate-table td {
  padding: 10px 12px;
  border-bottom: 1px solid #e6edf4;
  vertical-align: middle;
  font-size: 13px;
}

.aggregate-table tbody td {
  background: #ffffff;
  color: #17324d;
}

.aggregate-table tbody tr.highlight {
  background: rgba(var(--dashboard-accent-rgb), 0.06);
}

.aggregate-table tbody tr.highlight td {
  background: rgba(var(--dashboard-accent-rgb), 0.035);
}

.aggregate-table tbody tr:hover {
  background: rgba(var(--dashboard-accent-rgb), 0.025);
}

.aggregate-table tbody tr:hover td {
  background: rgba(var(--dashboard-accent-rgb), 0.018);
}

.item-label {
  min-width: 180px;
  max-width: 320px;
}

.amount-cell {
  font-variant-numeric: tabular-nums;
  font-weight: 700;
}

.share-meter {
  width: 100%;
  min-width: 120px;
  height: 8px;
  overflow: hidden;
  border-radius: 999px;
  background: rgba(var(--dashboard-accent-rgb), 0.12);
}

.share-meter span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, var(--dashboard-accent), var(--dashboard-accent-strong));
}

.difference-positive {
  color: #0b7a43;
  font-weight: 700;
}

.difference-negative {
  color: #c0392b;
  font-weight: 700;
}

@media (max-width: 1200px) {
  .header-card {
    grid-template-columns: 1fr;
  }

  .header-meta {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .summary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .filter-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .range-separator {
    display: none;
  }
}

@media (max-width: 768px) {
  .aggregate-dashboard {
    padding: 12px;
    gap: 12px;
  }

  .header-card,
  .panel-card {
    padding: 14px;
    border-radius: 14px;
  }

  .header-title-row,
  .panel-heading {
    flex-direction: column;
  }

  .header-main h1 {
    font-size: 24px;
  }

  .header-meta,
  .summary-grid,
  .filter-grid {
    grid-template-columns: 1fr;
  }

  .filter-actions {
    flex-direction: column;
  }

  .scope-switch {
    align-items: stretch;
  }

  .table-heading-actions {
    width: 100%;
    justify-content: space-between;
  }

  .scope-buttons {
    width: 100%;
  }

  .scope-button {
    flex: 1;
  }

  .chart-panel {
    height: 224px;
  }

  .aggregate-table {
    min-width: 640px;
  }

  .table-wrapper {
    max-height: 50vh;
  }
}
</style>
